import React, { FC, Ref, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { observer } from "mobx-react-lite"
import { Appearance, Dimensions, ImageStyle, Platform, TextStyle, TouchableOpacity, ViewStyle } from "react-native"
import { AutoImage, Button, Screen, Text } from "../../../components"
import { useStores } from "../../../models"
import { CompositeScreenProps, useLinkTo } from "@react-navigation/native"
import { Modal, useDisclose, View } from "native-base"
import { color, nativeBaseTheme, spacing } from "../../../theme"
import { resetPersistence } from "../../../navigators"
import AppIntroSlider from "react-native-app-intro-slider"
import { LinearGradient } from "expo-linear-gradient"
import config from "../../../utils/config"
import axios from "axios"
import _ from "lodash"
import moment from "moment"
import JalaAnalytic from "../../../utils/jala-analytic"
import { delay } from "../../../utils/delay"

const maskot = require("./maskot.png")
const titleQuiz = require("./title.png")

const guideImages = [
  {
    source: require('./guide1.png'),
    title: "startQuizScreen.guide1"
  },
  {
    source: require('./guide2.png'),
    title: "startQuizScreen.guide2"
  },
  {
    source: require('./guide3.png'),
    title: "startQuizScreen.guide3"
  },
  {
    source: require('./guide4.png'),
    title: "startQuizScreen.guide4"
  },
];

const ROOT: ViewStyle = {
  flexGrow: 1,
}

const CONTAINER: ViewStyle = {
  flex: 1,
  justifyContent: "center"
}

const INTRO_SLIDER: ViewStyle = {
  alignItems: Platform.OS === 'web' ? "center" : undefined,
  justifyContent: "center",
}

const TOP_CONTAINER: ViewStyle = {
  flex: 1,
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 80,
}

const ONBOARD: ImageStyle = {
  width: Dimensions.get("window").width * 280 / 320,
  height: Dimensions.get("window").width * 280 / 480,
  resizeMode: "contain",
  backgroundColor: color.background,
  borderRadius: 16
}

const ONBOARD_TEXT: TextStyle = {
  marginHorizontal: 16,
  marginTop: 20,
  textAlign: "center"
}

const BUTTON: ViewStyle = {
  backgroundColor: color.palette.white,
  paddingVertical: spacing[2],
  paddingHorizontal: spacing[2],
  borderRadius: 4,
  justifyContent: "center",
  alignItems: "center",
}

const FLEX: ViewStyle = {
  flex: 1
}

const PREV_BUTTON: ViewStyle = {
  ...FLEX,
  borderWidth: 0,
  backgroundColor: "transparent"
}

const CLOSE_BUTTON: ViewStyle = {
  position: "absolute",
  top: -8,
  right: -8
}

export const Slider: FC<{colorScheme?: "light" | "dark"}> = observer(function Slider(props){
  const { colorScheme = "light" || Appearance.getColorScheme(), children } = props;
  const { isOpen, onClose, onOpen } = useDisclose();

  const [ isEnd, setEnd ] = useState<boolean>(false);
  const [ disabled, setDisabled ] = useState<boolean>(true);

  const sliderRef: Ref<AppIntroSlider> = useRef<AppIntroSlider>(null);

  const remove = require("./remove.png");

  const activeDotStyle = useMemo(() => ({
    backgroundColor: colorScheme === "light" ? color.primary : nativeBaseTheme.colors.light[200]
  }), [colorScheme])

  const dotStyle = useMemo(() => ({
    backgroundColor: colorScheme === "light" ? color.palette.blueJalaLight : nativeBaseTheme.colors.dark[200]
  }), [colorScheme])

  const onPrevious = useCallback(() => {
    sliderRef.current.goToSlide(
      (sliderRef.current.state.activeIndex - 1) % guideImages.length,
      true
    );
  }, [])

  const onNext = useCallback(() => {
    setDisabled(false);
    if (sliderRef.current.state.activeIndex === 2) setEnd(true);
    sliderRef.current.goToSlide(
      (sliderRef.current.state.activeIndex + 1) % guideImages.length,
      true
    );
  }, [])

  const onDone = useCallback(() => {
    setEnd(false)
    setDisabled(true)
    onClose()
  }, [onClose])

  const keyExtractor = useCallback((item, index) => `slider-${index}`, []);

  const renderItem = useCallback((info) => (
    <View style={TOP_CONTAINER}>
      <AutoImage source={info.item.source} style={ONBOARD} />
      <Text
        tx={info.item.title}
        preset="body2"
        colorScheme={colorScheme}
        color={colorScheme === "light" ? color.palette.gray800 : undefined}
        numberOfLines={3}
        style={ONBOARD_TEXT}
      />
    </View>
  ), [colorScheme])


  return (<>
  <TouchableOpacity onPress={onOpen}>
      {children}
    </TouchableOpacity>
    <Modal isOpen={isOpen} onClose={onDone}>
      <View
        alignSelf="stretch"
        rounded="8px"
        backgroundColor={color.palette.white}
        mx="16px"
        position="relative"
      >
        <TouchableOpacity onPress={onDone} style={CLOSE_BUTTON}>
          <AutoImage source={remove} />
        </TouchableOpacity>
        <Text preset="display3" alignSelf="center" tx="startQuizScreen.howTo" my="16px" colorScheme={colorScheme} />
        <AppIntroSlider
          ref={sliderRef}
          renderItem={renderItem}
          data={guideImages}
          activeDotStyle={activeDotStyle}
          dotStyle={dotStyle}
          contentContainerStyle={INTRO_SLIDER}
          showPrevButton={false}
          showNextButton={false}
          showDoneButton={false}
          dotClickEnabled={false}
          scrollEnabled={false}
          keyExtractor={keyExtractor}
        />
        <View flexDirection="row" alignItems="center" mb="15" px="16px">
          <Button
            testID="jala:id/quiz-slider-previous-button"
            tx="homeScreen.tooltipPrevious"
            preset="outline"
            disabled={disabled}
            colorScheme={colorScheme}
            textColor={disabled ? color.palette.gray500 : color.primary}
            style={PREV_BUTTON}
            onPress={onPrevious}
          />
          <View width="16px" />
          <Button
            testID="jala:id/quiz-slider-next-button"
            tx={isEnd ? "common.done" : "common.next"}
            colorScheme={colorScheme}
            style={FLEX}
            onPress={isEnd ? onDone : onNext}
          />
        </View>
      </View>
    </Modal>
  </>)
})

const cacheKey = Platform.OS === 'web' ? new URLSearchParams(window.location.search).get('cache_key') : null
const hash = Platform.OS === 'web' ? new URLSearchParams(window.location.hash.replace('#', '')) : new URLSearchParams()

export const StartScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function StartScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), navigation } = props
  const linkTo = useLinkTo()

  // Pull in one of our MST stores
  const { tokenStore, appStore, userStore, quizStore } = useStores()

  useEffect(() => {
    if (Platform.OS !== 'web' || !navigation.isFocused()) return;

    const { BASE_URL, OAUTH2_URL, CLIENT_ID } = config()

    const accessToken = hash.get('access_token');
    if (cacheKey) {
      tokenStore.requestTokenByCacheKey(cacheKey);
    } else if (accessToken) {
      tokenStore.setToken({
        token_type: hash.get('token_type'),
        expires_in: Number(hash.get('expires_in')),
        access_token: hash.get('access_token'),
        refresh_token: hash.get('refresh_token'),
      })
      window.location.hash = ''
      userStore.getUser(false)
        .then(() => {
          if (userStore.user) {
            JalaAnalytic.setUserId(userStore.user.id, userStore.user);
            // Manually Trigger. :(
            JalaAnalytic.postHog.screen('Quiz.Start');
          }
        })
    } else {
      const url = new URL(`${OAUTH2_URL}/authorize`);
      const searchParams = new URLSearchParams()
      searchParams.set('client_id', CLIENT_ID);
      searchParams.set('redirect_uri', `${window.location.origin}/`);
      searchParams.set('response_type', 'token');
      searchParams.set('scope', '*');
      url.search = searchParams.toString();

      const urlLogout = new URL(`${BASE_URL}/logout`);
      const searchParamsLogout = new URLSearchParams()
      searchParamsLogout.set('redirect', url.toString());
      urlLogout.search = searchParamsLogout.toString();

      window.location.href = urlLogout.toString();
      resetPersistence();
    }
  }, [navigation, tokenStore, userStore])

  useEffect(() => {
    if (tokenStore.token)
      console.log('Go to main page')
  }, [tokenStore.token])

  const fetchData = useCallback(async () => {
    const { AIRTABLE_BASE_ID, AIRTABLE_API_KEY, AIRTABLE_FIELD } = config();
    return await axios.get(`https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${AIRTABLE_FIELD}`, {
      method: "get",
      headers: {
        'Content-type': 'application/json',
        'Authorization': `Bearer ${AIRTABLE_API_KEY}`,
      }
    })
      .then(response => {
        const { data } = (response || {})
        if (response.status === 200) quizStore.set('questions', _.take(data.records, 10))
        return response;
      })
      .catch(err => console.warn(err))
  }, [quizStore])

  useEffect(() => {
    appStore.setLocale("id");
    fetchData().catch();
  }, [appStore, fetchData])

  const handleNavigate = useCallback(() => {
    fetchData()
      .then(async () => {
        const date = moment().valueOf();
        quizStore.set('times', [date])
        await delay(10)
        linkTo('/Quiz.Detail')
      })
  }, [fetchData, linkTo, quizStore])

  // const onPress = useCallback(() => {
  //   tokenStore.requestCacheKey()
  //     .then((res) => {
  //       window.location.href = `/?cache_key=${tokenStore.cacheKey}`
  //     })
  // }, [tokenStore])

  return (
    <Screen
      style={ROOT}
      preset="scroll"
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
    >
      {/* {!tokenStore.token && <View flex={1} alignItems="center" justifyContent="center" width={Dimensions.get('window').width - 32}>
        <JalaLoading />
      </View>} */}
      {/* {__DEV__ && <Button text="Login" colorScheme={colorScheme} onPress={onPress} />} */}
      <LinearGradient
        colors={['#0082F3', '#45AAE3']}
        style={CONTAINER}
        start={{ x: 0.0, y: 0.0 }}
        end={{ x: 1, y: 1}}
      >
      <View justifyContent="center" mt="10px" alignItems="center" px="16px">
        <AutoImage source={maskot} />
        <AutoImage source={titleQuiz} />
        <Text
          preset="title3"
          mt="10px"
          textAlign="center"
          backgroundColor={color.palette.white}
          tx="startQuizScreen.challange"
          color={color.palette.white}
          colorScheme={colorScheme}
        />
      </View>
      <View px="35px" mt="59px">
        <Button tx="startQuizScreen.start" colorScheme={colorScheme} style={{backgroundColor: color.palette.orange500}} textColor={color.palette.white} onPress={handleNavigate} />
        <View height="20px" />
        <Slider>
          <View style={BUTTON}>
            <Text preset="title3" tx="startQuizScreen.howTo" colorScheme={colorScheme} color={color.primary} />
          </View>
        </Slider>
      </View>
      </LinearGradient>
    </Screen>
  )
})
